import { createContext, useContext, useMemo, useRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconAiGradient } from '@components/icons/ai-gradient';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconInput } from '@components/icons/input';
import { IconReplace } from '@components/icons/replace';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { SizedRelativeSpinner } from '@components/loading-spinner/loading-spinner';
import { Modal, useModal } from '@components/modal';
import { CloseButton, MODAL_CONTENT_CLASSNAME } from '@components/modal/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingGroup,
  MappableVariable,
  transformInput,
  findUnknownVariables,
  useVariableStyledContent,
  mapVariableGroups,
} from '@components/text-editors/addons/field-mapping';
import { TextAreaDynamic } from '@components/textarea/textarea-dynamic';
import { showError } from '@components/toaster';
import { useStickyAutoScroll } from '@hooks/use-sticky-auto-scroll';
import { useScopedTranslate } from '@locale/translate';
import { scrollbarStyle, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import { useAiInputAssistCreateNewChat, useAiInputAssistSendMessage } from './ai-input-assist-api';
import { AiAssistRoles, AiInputProps, ChatMessage, NewChatMessage } from './types';

export const useAiInputAssistModal = () => useModal(AiInputModal, { variant: 'EXTRA_LARGE' });

const GENERATING_ID = 'generating';
const TIMEOUT_DURATION = 30000;

let MESSAGE_STORE: Record<string, Array<ChatMessage>> = {};

const getCachedMessages = (uuid: string) => {
  const cachedMessages = uuid ? MESSAGE_STORE[uuid] ?? [] : [];
  return cachedMessages;
};

const VariableContext = createContext<Record<string, MappableVariable>>({});

type AiInputModalProps = AiInputProps & {
  variableGroups?: Array<FieldMappingGroup>;
  editorUuid?: keyof typeof MESSAGE_STORE;
  onSave: (value: string) => void;
  useWebSocket: (url: string, onMessage: (data: MessageEvent<string>) => void) => void;
  plaintext?: boolean;
};

const AiInputModal = ({
  onSave,
  threadInstructions,
  generateChoices,
  variableGroups,
  editorUuid = '',
  useWebSocket,
  plaintext,
}: AiInputModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [value, setValue] = useState('');
  const [messages, setMessages] = useState<Array<ChatMessage | NewChatMessage>>(
    getCachedMessages(editorUuid)
  );
  const currentMessageRef = useRef('');
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const chatContentRef = useRef<HTMLDivElement | null>(null);
  const threadId = useMemo(
    () => (messages as Array<NewChatMessage>).find((message) => message.threadId)?.threadId ?? '',
    [messages]
  );
  const lastMessage = useRef('');

  const { onScroll: handleChatScroll } = useStickyAutoScroll({
    containerRef: chatContainerRef,
    contentRef: chatContentRef,
    resetKey: threadId,
  });

  const createChat = useAiInputAssistCreateNewChat();
  const sendMessage = useAiInputAssistSendMessage();

  const newChatWebsocketId = useRef(uuid()).current;
  const threadWebsocketId = useRef(uuid()).current;

  const variables = mapVariableGroups(variableGroups);

  const variableMap = Object.keys(variables).reduce((acc, key) => {
    acc[key] = variables[key].label;
    return acc;
  }, {} as Record<string, string>);

  const handleAddNewMessages = (newMessages: ChatMessage[], newMessage: ChatMessage) => {
    const parsedMessages = newMessages.map((message) =>
      message.id === GENERATING_ID ? { ...newMessage, id: uuid() } : message
    );
    MESSAGE_STORE = { [editorUuid]: parsedMessages };
    setMessages(parsedMessages);
  };

  const addMessage = (message: string) => {
    lastMessage.current = message;
    setValue('');
    const newMessages = [
      ...messages,
      { message, id: uuid(), role: 'user' as AiAssistRoles },
      { message: '', id: GENERATING_ID, role: 'assistant' as AiAssistRoles },
    ];
    setMessages(newMessages);
  };

  const handleAddMessage = async (message: string = value) => {
    if (isLoading) {
      return;
    }
    addMessage(message);
    sendMessage.mutateAsync({
      prompt: value,
      threadId,
      variableMap,
      websocketId: threadWebsocketId,
    });
    setIsLoading(true);
  };

  const handleCreateChat = async (message: string = value) => {
    addMessage(message);
    createChat.mutateAsync({
      prompt: message,
      variableMap,
      threadInstructions,
      websocketId: newChatWebsocketId,
    });
    setIsLoading(true);
  };

  const handleSendMessage = (message?: string) => {
    if (isLoading) {
      return;
    }
    const currentMessageId = uuid();
    currentMessageRef.current = currentMessageId;
    if (threadId) {
      handleAddMessage(message);
    } else {
      handleCreateChat(message);
    }
    setTimeout(() => {
      if (currentMessageRef.current === currentMessageId) {
        setIsLoading(false);
        setMessages((prevMessages) =>
          prevMessages.filter((message) => message.id !== GENERATING_ID)
        );
        showError(t('timeout'));
      }
    }, TIMEOUT_DURATION);
  };

  const handleWebsocketMessage = (data: MessageEvent<string>) => {
    currentMessageRef.current = '';
    const AIMessage = JSON.parse(data.data);
    if (AIMessage.content.hasError) {
      showError(AIMessage.content.message);
      setIsLoading(false);
      return;
    }
    // Check if there are unknown variables in the AI message, and if it is, we try again, given we haven't done that yet
    const unknownVariables = findUnknownVariables(AIMessage.content.message, variables);
    if (unknownVariables.length > 0 && lastMessage.current) {
      sendMessage.mutateAsync({
        prompt: lastMessage.current,
        threadId: AIMessage.threadId,
        variableMap,
        websocketId: threadWebsocketId,
      });
      lastMessage.current = '';
      return;
    }
    handleAddNewMessages(messages, AIMessage.content);
    setIsLoading(false);
  };

  useWebSocket(`content-ai/ai-agents/${newChatWebsocketId}/`, (data: MessageEvent<string>) => {
    handleWebsocketMessage(data);
  });

  useWebSocket(
    `content-ai/ai-agents/thread/${threadWebsocketId}/`,
    (data: MessageEvent<string>) => {
      handleWebsocketMessage(data);
    }
  );

  return (
    <VariableContext.Provider value={variables}>
      <StyledModal>
        <Modal.Content>
          <Stack space={6} orientation="horizontal" style={{ height: '100%' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Stack
                orientation="horizontal"
                justify="space-between"
                align="center"
                style={{ marginLeft: pxToRem(-16), marginRight: pxToRem(-16) }}
              >
                <Stack space={2} orientation="horizontal" align="center">
                  <IconAiGradient />
                  <Text text={t('title')} font="heading1" />
                </Stack>
                <Stack space={2} orientation="horizontal" align="center">
                  <Text aiGradient text={t('aiModel')} />
                  <CloseButton />
                </Stack>
              </Stack>
              <Separator />
              <ChatContainer ref={chatContainerRef} onScroll={handleChatScroll}>
                <Stack ref={chatContentRef} space={4}>
                  {messages.length === 0 && generateChoices ? (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {generateChoices.map((choice) => (
                        <Button
                          key={choice.title}
                          text={choice.title}
                          variant="secondary"
                          iconLeft={<IconReplace />}
                          onClick={() => handleCreateChat(choice.prompt)}
                        />
                      ))}
                    </div>
                  ) : (
                    messages.map((message) => (
                      <Message
                        key={message.id}
                        message={message}
                        onSave={onSave}
                        isLoading={isLoading}
                        plaintext={plaintext}
                        variableGroups={variableGroups}
                      />
                    ))
                  )}
                </Stack>
              </ChatContainer>
              <InputContainer>
                <TextAreaDynamic
                  autoFocus
                  placeholder={t('aiPlaceholder')}
                  onChange={setValue}
                  value={value}
                  style={{
                    minHeight: pxToRem(58),
                    maxHeight: pxToRem(256),
                    paddingRight: pxToRem(56),
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <IconButton
                  icon={<IconArrowRight />}
                  variant="blue-white"
                  onClick={() => handleSendMessage(value)}
                  style={{ position: 'absolute', right: pxToRem(9), bottom: pxToRem(9) }}
                  disabled={!value || isLoading}
                />
              </InputContainer>
            </div>
          </Stack>
        </Modal.Content>
      </StyledModal>
    </VariableContext.Provider>
  );
};

const InputContainer = styled.div({
  position: 'relative',
  marginTop: pxToRem(16),
  textarea: {
    paddingTop: pxToRem(16),
  },
});

const ChatContainer = styled.div({
  flex: 1,
  overflow: 'auto',
  paddingTop: pxToRem(16),
  whiteSpace: 'pre-wrap',
  // Using justify-content: flex-end doesn't work well with scrollbars, so we use margin-top: auto instead
  '&>:first-child': {
    marginTop: 'auto',
  },
  ...scrollbarStyle,
});

const StyledModal = styled(Modal)({
  [`.${MODAL_CONTENT_CLASSNAME}`]: {
    marginTop: pxToRem(-24),
    overflow: 'hidden',
  },
  '.separator': {
    width: '200%',
    marginLeft: pxToRem(-40),
    marginTop: pxToRem(16),
  },
});

type ChatMessageProps = Pick<AiInputModalProps, 'onSave' | 'plaintext' | 'variableGroups'> & {
  message: ChatMessage;
  isLoading: boolean;
};

const Message = (props: ChatMessageProps) => {
  const { message, isLoading } = props;
  if (message.id === GENERATING_ID && isLoading) {
    return <GeneratingMessage />;
  }
  if (message.role === 'assistant') {
    return <TokenedMessageHandler {...props} />;
  }
  return <MessageContainer>{message.message}</MessageContainer>;
};

const TokenedMessageHandler = ({
  message,
  onSave,
  plaintext,
  variableGroups,
}: ChatMessageProps) => {
  const variables = useContext(VariableContext);
  const { t } = useScopedTranslate(locale);

  const { cleanMessage, styledContent } = useVariableStyledContent({
    content: message.message,
    variables,
    variableGroups,
    icon: IconTextFormatLink,
    hideRemove: true,
    includeFullAttrs: false,
  });

  const handleSave = () => {
    const parsedText = plaintext ? cleanMessage : cleanMessage.replace(/\n/g, '<br/>');
    const transformed = transformInput({ value: parsedText, variables });

    onSave(transformed.value);
  };

  return (
    <MessageContainer $aiGradient={message.role === 'assistant'}>
      <Stack space={2}>
        {styledContent}
        <Stack orientation="horizontal" justify="flex-end" align="center" space={4}>
          <Text font="semibold14" aiGradient text={t('aiOutput')} />
          <Button
            variant="secondary"
            iconRight={<IconInput />}
            text={t('placeContent')}
            onClick={() => handleSave()}
          />
        </Stack>
      </Stack>
    </MessageContainer>
  );
};

const GeneratingMessage = () => {
  const { t } = useScopedTranslate(locale);

  return (
    <MessageContainer style={{ display: 'flex', justifyContent: 'center' }}>
      <Stack orientation="horizontal" space={4} align="center">
        <div>
          <SizedRelativeSpinner size={24} />
        </div>
        <Text aiGradient text={t('generating')} font="semibold14" />
      </Stack>
    </MessageContainer>
  );
};

const MessageContainer = styled.div<{ $aiGradient?: boolean }>(({ $aiGradient }) => {
  const borderRadius = pxToRem(8);
  return {
    borderRadius,
    border: `${pxToRem(1)} solid ${theme.color.border10}`,
    padding: pxToRem(16),
    ...($aiGradient && {
      position: 'sticky',
      '&&:before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: -1,
        padding: pxToRem(1), // the border thickness
        borderRadius,
        background: 'linear-gradient(170deg, #337FFF, #E039CE)',
        mask: `
        linear-gradient(#000 0 0) exclude,
        linear-gradient(#000 0 0) content-box
      `,
        WebkitMask: `
        linear-gradient(#000 0 0) exclude,
        linear-gradient(#000 0 0) content-box
      `,
      },
    }),
  };
});

const locale = {
  en: {
    title: 'Journey Helper',
    aiPlaceholder:
      'Type a short summary of what you want to say, and I will create a ready-to-use message',
    chats: 'Chats',
    newChat: 'New chat',
    generating: 'Generating',
    aiOutput: 'AI Output',
    placeContent: 'Place content',
    aiModel: 'AI Model • GPT-5.1',
    timeout: 'Request timed out. Please try again.',
  },
};
